// Where the page is NOW, following navigation — and a way to rewrite the URL
// without the router losing track of it.
//
// Shared by the two surfaces that read the `?figma=` marker the Figma OAuth
// callback returns with: `FigmaLinkPanel` (a Connect started beside a link in a
// chat) and `components/FigmaConnection` (a Connect started on the account
// page). One hook, so the two cannot drift on how a marker is found or stripped.

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useRouter } from "@tanstack/react-router";

export interface HereLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

const SSR_HREF = "/";

/** `pathname + search + hash` → the three halves. The snapshot is one string so
 *  `useSyncExternalStore` can compare it by value. */
const splitHref = (href: string): HereLocation => {
  const hashAt = href.indexOf("#");
  const beforeHash = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : href.slice(hashAt);
  const queryAt = beforeHash.indexOf("?");
  return queryAt === -1
    ? { pathname: beforeHash, search: "", hash }
    : {
        pathname: beforeHash.slice(0, queryAt),
        search: beforeHash.slice(queryAt),
        hash,
      };
};

/**
 * Where the page is NOW, following navigation.
 *
 * This used to be a `useMemo` over `window.location` with no dependency that
 * moves, so it was frozen at mount: a user who navigated inside the SPA and
 * then pressed Connect was sent back to the page they had left, and a `?figma=`
 * marker that arrived later was never seen.
 *
 * The router's own history is the source when there is a router — its
 * `subscribe` fires on every push, replace and back/forward, which is exactly
 * what `useRouterState` rides. `useRouter({ warn: false })` rather than
 * `useRouterState`, because the latter throws outside a `<RouterProvider>`, and
 * Focus renders this panel in component tests that mount no router. With no
 * router, `popstate` on `window` is the fallback.
 */
export const useHereLocation = (): {
  readonly here: HereLocation;
  readonly replace: (href: string) => void;
} => {
  const router = useRouter({ warn: false }) as
    | ReturnType<typeof useRouter>
    | undefined;
  const history = router?.history;
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (history !== undefined) return history.subscribe(onChange);
      if (typeof window === "undefined") return () => {};
      window.addEventListener("popstate", onChange);
      return () => window.removeEventListener("popstate", onChange);
    },
    [history],
  );
  const snapshot = useCallback((): string => {
    if (history !== undefined) {
      const l = history.location;
      return `${l.pathname}${l.search}${l.hash}`;
    }
    if (typeof window === "undefined") return SSR_HREF;
    const l = window.location;
    return `${l.pathname}${l.search}${l.hash}`;
  }, [history]);
  const href = useSyncExternalStore(subscribe, snapshot, () => SSR_HREF);
  const here = useMemo(() => splitHref(href), [href]);
  const replace = useCallback(
    (next: string) => {
      // Through the router when there is one, so its own location (and every
      // `useRouterState` reader) moves with the URL; a bare `replaceState`
      // would change the address bar behind the router's back.
      if (history !== undefined) {
        history.replace(next, history.location.state);
        return;
      }
      if (typeof window === "undefined") return;
      window.history.replaceState(window.history.state, "", next);
    },
    [history],
  );
  return { here, replace };
};
